import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useLanguage } from '@/hooks/useLanguage';
import { MAIN_SITE_URL } from '@/data/categories';
import { posts, type LocalizedText } from '@/data/posts';
import { ARTICLE_IMAGES } from '@/data/images';
import JsonLd from '@/components/feature/JsonLd';
import { buildArticleSchema, buildFaqSchema } from '@/data/seo';

export default function Post() {
  const { lang, slug } = useParams();
  const { t } = useTranslation();
  useLanguage(lang);

  const post = posts.find((p) => p.slug === slug);
  const currentLang = lang ?? 'en';
  const pick = (lt: LocalizedText) => lt[currentLang as keyof LocalizedText] || lt.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="mx-auto max-w-xl px-4 md:px-6 text-center">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              {t('buttons.backHome')}
            </h1>
            <Link
              to={`/${lang}`}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('buttons.backHome')}
              <i className="ri-arrow-left-line" />
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const image = ARTICLE_IMAGES[post.slug] ?? ARTICLE_IMAGES['spreadsheet-guide'];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={buildArticleSchema({
          lang: currentLang,
          path: `/${slug}`,
          headline: pick(post.title),
          description: pick(post.description),
          image,
          datePublished: '2026-08-21',
        })}
      />
      <JsonLd data={buildFaqSchema(post.faq)} />
      <Navbar />
      <main className="pt-16">
        <article className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {t('nav.home')}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{pick(post.title)}</span>
          </nav>

          <h1 className="mt-4 font-heading font-bold text-3xl md:text-5xl text-foreground-950 leading-tight">
            {pick(post.title)}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              USFansLink Editorial Team
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {post.published}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {post.readingTime}
            </span>
          </div>

          <div className="mt-6 w-full h-56 md:h-80 rounded-lg overflow-hidden">
            <img src={image} alt={pick(post.title)} className="w-full h-full object-cover object-top" />
          </div>

          <p className="mt-8 text-base md:text-lg text-foreground-700 leading-relaxed">{post.intro}</p>

          <div className="mt-10 space-y-10">
            {post.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">{s.title}</h2>
                <p className="mt-3 text-foreground-600 leading-relaxed">{s.body}</p>
                {s.list && (
                  <ul className="mt-4 space-y-2.5">
                    {s.list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                        <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <section className="mt-12">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground-950">
              {t('article.faqTitle')}
            </h2>
            <div className="mt-5 space-y-3">
              {post.faq.map((f) => (
                <div key={f.q} className="rounded-lg bg-background-50 border border-background-200 p-5">
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900">{f.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-lg bg-primary-50 border border-primary-200 p-6 md:p-8 text-center">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
              {t('article.ctaTitle')}
            </h2>
            <p className="mt-3 text-foreground-600">{t('article.ctaBody')}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('article.ctaButton')}
              <i className="ri-arrow-right-line" />
            </a>
            <Link
              to={`/${lang}`}
              className="mt-4 block text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              ← {t('buttons.backHome')}
            </Link>
          </section>

          <p className="mt-8 text-xs text-foreground-500 leading-relaxed">{t('article.disclaimer')}</p>
        </article>
      </main>
      <Footer />
    </div>
  );
}